import type * as PdfJs from 'pdfjs-dist';

/**
 * The one place `pdfjs-dist` and its worker are set up.
 *
 * Nothing is loaded and no worker is started until something draws a PDF: the library is a
 * large chunk and its worker a thread, and most readers never open the two places that need
 * them (a PDF attachment's preview image in the viewer, and the report preview).
 */

type PdfJsModule = typeof PdfJs;

let library: Promise<PdfJsModule> | undefined;

const startWorker = async (): Promise<Worker> => {
  const { default: PdfWorker } = await import('pdfjs-dist/build/pdf.worker.min.mjs?worker');

  return new PdfWorker();
};

/**
 * `pdfjs-dist`, with the worker every `getDocument` call uses unless it is given its own.
 * The first call loads both; every later one answers the same module.
 */
export const loadPdfjs = (): Promise<PdfJsModule> => {
  library ??= Promise.all([import('pdfjs-dist'), startWorker()]).then(([pdfjs, port]) => {
    pdfjs.GlobalWorkerOptions.workerPort = port;

    return pdfjs;
  });

  // A load that failed (a chunk that could not be fetched) is tried again by the next caller.
  library.catch(() => {
    library = undefined;
  });

  return library;
};

/** A worker of a caller's own, to pass to `getDocument` as `worker`. */
export interface OwnPdfWorker {
  worker: PdfJs.PDFWorker;
  /** Stops the thread. Call it once every document loaded through the worker is destroyed. */
  destroy: () => void;
}

/**
 * A worker apart from the shared one, for a caller that loads one document after another
 * and destroys the old ones.
 *
 * Destroying a loading task that was given no worker destroys the wrapper around the shared
 * port, under whatever else is reading through it at that moment. A task that was handed a
 * worker leaves it alone, so a caller with its own decides when it ends.
 */
export const createPdfWorker = async (): Promise<OwnPdfWorker> => {
  const [pdfjs, port] = await Promise.all([loadPdfjs(), startWorker()]);
  // `fromPort` and not the constructor, whose declared parameters leave the port out. For a
  // port no worker has been made from yet, the two are the same call.
  const worker = pdfjs.PDFWorker.fromPort({ port }) as PdfJs.PDFWorker;

  return {
    worker,
    destroy: () => {
      worker.destroy();
      // A worker made from a port does not own the thread behind it.
      port.terminate();
    },
  };
};
